import {Link, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'

import type {FunctionComponent} from 'react'
import {useMemo, useRef} from 'react'
import {useReportWidgetClick} from '~/hooks/analytic-hooks'
import {IconChevronRight} from '~/icons/brg-icon-chevron-right'
import type {Breadcrumb, RootLoaderResponse} from '~/types'

const DEFAULT_BREADCRUMBS = [
  {
    url: '/',
    name: 'Home',
  },
]

interface BreadcrumbsProps {
  breadcrumbs: Breadcrumb[]
  productTitle: string
}
const Breadcrumbs: FunctionComponent<BreadcrumbsProps> = ({breadcrumbs, productTitle}) => {
  const {deviceType, featureFlags, hostname} = useRouteLoaderData('root') as RootLoaderResponse
  const breadcrumbsContainerRef = useRef<HTMLDivElement>(null)

  const reportWidgetClick = useReportWidgetClick()

  const fullBreadcrumbs = useMemo(() => {
    const currentBreadcrumb = {
      url: '#',
      name: productTitle,
    }

    return [
      ...DEFAULT_BREADCRUMBS,
      ...breadcrumbs.map((breadcrumb) => ({
        name: breadcrumb.name,
        url: `/${breadcrumb.slug?.split('/').slice(-1)[0] || ''}`,
      })),
      currentBreadcrumb,
    ]
  }, [breadcrumbs, productTitle])

  function isLastBreadcrumb(currIndex: number) {
    return currIndex === fullBreadcrumbs.length - 1
  }

  return (
    breadcrumbs &&
    breadcrumbs.length !== 0 && (
      <div
        ref={breadcrumbsContainerRef}
        className={clsx(
          'mt-2 mobile:mt-1',
          featureFlags?.showNewHeaderAndCookieUi && deviceType && deviceType !== 'desktop' ? 'line-clamp-1' : '',
          'h-4',
        )}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: fullBreadcrumbs.map((breadcrumb, index) => {
                const path = breadcrumb.url === '#' ? `/${productTitle}` : breadcrumb.url
                return {
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    '@id': `https://${hostname}${path}`,
                    name: breadcrumb.name,
                  },
                }
              }),
            }),
          }}
        ></script>
        <div
          data-testid="breadcrumbs"
          className="content-container no-scrollbar mx-auto flex w-full items-center overflow-x-scroll text-sm leading-4 last:cursor-default [&>*:last-child]:text-[#363636] [&>*]:text-[#9C9C9C]"
        >
          {fullBreadcrumbs.map((breadcrumb, index) => {
            const urlBreadcrumbLink = isLastBreadcrumb(index) ? '#' : breadcrumb.url
            return (
              <div key={`${breadcrumb}-${index}`} className="flex shrink-0 flex-row">
                <Link
                  to={urlBreadcrumbLink}
                  replace={!isLastBreadcrumb(index)}
                  aria-label={`go to ${breadcrumb.name}`}
                  className="shrink-0 capitalize"
                  onClick={() => {
                    reportWidgetClick({
                      widget_name: 'breadcrumbs',
                      widget_place: 'top',
                      trigger_type: urlBreadcrumbLink,
                      variation: breadcrumb.name,
                    })
                  }}
                >{`${breadcrumb.name}`}</Link>
                {isLastBreadcrumb(index) ? null : (
                  <div className="flex px-1">
                    <IconChevronRight className="size-4" fill="#9c9c9c" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  )
}

export default Breadcrumbs
